
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Check, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { validatePassword, validatePasswordMatch } from '@/lib/validation/passwordValidation';

const UpdatePassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { updatePassword, user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Password requirements check
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };

  useEffect(() => {
    // Allow both logged-in users and users coming from password reset emails
    // Users from password reset emails will have a session but might not have full user data loaded yet
    if (!user && !authLoading) {
      // Only redirect if we're sure there's no session and not still loading
      const checkSession = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/login');
        }
      };
      checkSession();
    }
  }, [user, authLoading, navigate]);

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePasswordMatch(password, confirmPassword)) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive"
      });
      return;
    }

    const validation = validatePassword(password);
    if (!validation.isValid) {
      toast({
        title: "Error",
        description: validation.errors[0],
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await updatePassword(password);
      
      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive"
        });
      } else {
        // Redirect to appropriate page after successful password update
        setTimeout(() => {
          if (user) {
            navigate('/dashboard');
          } else {
            navigate('/login');
          }
        }, 1500);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: t('auth.genericError'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-3 rounded-full w-fit mx-auto mb-4">
            <Lock className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold">{t('auth.updatePassword')}</CardTitle>
          <p className="text-muted-foreground">
            {t('auth.enterNewPassword')}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-sm font-semibold">
                {t('auth.newPassword')}
              </Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.newPassword')}
                  className="h-12 border-border rounded-sm focus:border-primary focus:ring-1 focus:ring-primary pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Password requirements checklist */}
            {password && (
              <div className="space-y-1 text-sm">
                <PasswordRequirement met={passwordChecks.length} text="At least 8 characters" />
                <PasswordRequirement met={passwordChecks.uppercase} text="One uppercase letter" />
                <PasswordRequirement met={passwordChecks.lowercase} text="One lowercase letter" />
                <PasswordRequirement met={passwordChecks.number} text="One number" />
                <PasswordRequirement met={passwordChecks.special} text="One special character" />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-sm font-semibold">
                {t('auth.confirmPassword')}
              </Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('auth.confirmPassword')}
                className="h-12 border-border rounded-sm focus:border-primary focus:ring-1 focus:ring-primary"
                required
              />
            </div>
            
            <Button type="submit" className="w-full tesla-button" disabled={loading}>
              {loading ? t('common.loading') : t('auth.updatePassword')}
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              className="text-sm text-muted-foreground"
            >
              {t('auth.backToSignIn')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Helper component for password requirements
const PasswordRequirement = ({ met, text }: { met: boolean; text: string }) => (
  <div className="flex items-center gap-2">
    {met ? (
      <Check className="h-4 w-4 text-green-500" />
    ) : (
      <X className="h-4 w-4 text-muted-foreground" />
    )}
    <span className={met ? 'text-green-600' : 'text-muted-foreground'}>{text}</span>
  </div>
);

export default UpdatePassword;
